import { Button } from "@faire/slate/components/button";
import { Select } from "@faire/slate/components/select";
import { SelectionCardGroup } from "@faire/slate/components/selection-card";
import { useMemo, useState, type ChangeEvent } from "react";

import {
  getPrepackCountOptions,
  getTotalItemCountOptions,
  PREPACK_PRODUCT,
  PREPACKS_PURCHASED,
  type PrePackReportContext,
} from "./prePackPolicyData";
import { RetailerMobileNav } from "./RetailerMobileNav";

type AllItemsAffectedChoice = "yes" | "no";

type PrePackAffectedScreenProps = {
  onContinue?: (context: PrePackReportContext) => void;
};

export const PrePackAffectedScreen = ({
  onContinue,
}: PrePackAffectedScreenProps) => {
  const [prepacksAffected, setPrepacksAffected] = useState<string>("");
  const [allItemsAffected, setAllItemsAffected] = useState<
    AllItemsAffectedChoice | ""
  >("");
  const [totalItemsAffected, setTotalItemsAffected] = useState<string>("");

  const prepackCount = Number(prepacksAffected);
  const hasPrepackSelection = prepacksAffected !== "";
  const showAllItemsQuestion = hasPrepackSelection;
  const showTotalItemsQuestion =
    showAllItemsQuestion && allItemsAffected === "no";

  const prepackOptions = useMemo(() => getPrepackCountOptions(), []);
  const totalItemOptions = useMemo(
    () => (hasPrepackSelection ? getTotalItemCountOptions(prepackCount) : []),
    [hasPrepackSelection, prepackCount]
  );

  const handlePrepackChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setPrepacksAffected(event.target.value);
    setAllItemsAffected("");
    setTotalItemsAffected("");
  };

  const handleAllItemsChange = (choice: AllItemsAffectedChoice) => {
    setAllItemsAffected(choice);
    if (choice === "yes") {
      setTotalItemsAffected("");
    }
  };

  const canContinue =
    hasPrepackSelection &&
    allItemsAffected !== "" &&
    (allItemsAffected === "yes" || totalItemsAffected !== "");

  return (
    <div className="bg-fs-surface-primary flex min-h-full flex-col">
      <RetailerMobileNav progressFraction={0.25} />
      <div className="flex flex-1 flex-col gap-(--slate-spacing-section-medium) px-(--slate-spacing-section-medium) pt-(--slate-spacing-section-medium) pb-(--slate-spacing-section-large)">
        <h1 className="type-fs-page-heading text-fs-text-primary">
          What was affected?
        </h1>

        <div className="flex flex-col gap-(--slate-spacing-element-large)">
          <div className="flex gap-(--slate-spacing-element-medium)">
            <img
              alt=""
              className="size-[72px] shrink-0 object-cover"
              src={PREPACK_PRODUCT.imageUrl}
            />
            <div className="flex min-w-0 flex-col gap-[2px]">
              <span className="type-fs-paragraph-medium text-fs-text-primary">
                {PREPACK_PRODUCT.name}
              </span>
              <span className="type-fs-paragraph text-fs-text-primary">
                {PREPACK_PRODUCT.variant}
              </span>
              <div className="type-fs-paragraph text-fs-text-primary flex gap-(--slate-dimensions-2)">
                <span>{PREPACK_PRODUCT.prepackLabel}</span>
                <span>{PREPACK_PRODUCT.prepackRatio}</span>
              </div>
            </div>
          </div>

          <Select
            label="How many prepacks were affected?"
            placeholder="Select"
            helper={`${PREPACKS_PURCHASED} prepacks purchased`}
            options={prepackOptions}
            value={prepacksAffected}
            onChange={handlePrepackChange}
          />

          {showAllItemsQuestion ? (
            <div className="flex flex-col gap-(--slate-dimensions-2)">
              <span className="type-fs-paragraph text-fs-text-primary">
                Were all items in these prepacks affected?
              </span>
              <SelectionCardGroup
                name="prepack-all-items-affected"
                items={[
                  {
                    label: "Yes",
                    checked: allItemsAffected === "yes",
                    onChange: () => handleAllItemsChange("yes"),
                  },
                  {
                    label: "No, only some",
                    checked: allItemsAffected === "no",
                    onChange: () => handleAllItemsChange("no"),
                  },
                ]}
              />
            </div>
          ) : null}

          {showTotalItemsQuestion ? (
            <Select
              label="How many items total?"
              placeholder="Select"
              options={totalItemOptions}
              value={totalItemsAffected}
              onChange={(event) => setTotalItemsAffected(event.target.value)}
            />
          ) : null}
        </div>
      </div>

      <div className="bg-fs-surface-primary sticky bottom-0 px-(--slate-spacing-section-medium) pt-(--slate-dimensions-2) pb-(--slate-spacing-section-medium)">
        <Button
          disabled={!canContinue}
          fullWidth
          onClick={() =>
            onContinue?.({
              allItemsAffected: allItemsAffected as AllItemsAffectedChoice,
            })
          }
          variant="primary"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
